// src/modules/units/tyres/views/detail/tyredetail.js
//
// Tyre detail — multi-pane view (Specification, Condition & wear, Live telemetry,
// Distance breakdown) plus the action-history timeline and a header action bar.
// This is the orchestrator: it paints immediately from the row object the
// register already has (no blank screen), then fetches the authoritative tyre
// record and the action history in parallel and swaps each region in as it
// arrives. Most action buttons delegate to ctx.onAction; Rotate is the exception
// — it's a zero-data one-shot, so it's confirmed and written inline here (date
// only); the refresh then arrives via the CHANGED subscription rather than being
// triggered by hand. All markup comes from tyredetail.view.js.

import { fetchTyre, fetchTyreActions, createTyreAction } from '../tyres.api.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';
import { apiActionType, actionLogMessage } from '../../shared/tyre-action-schema.js';
import { todayInputValue, isoFromDateInput } from '../../shared/tyre-dates.js';
import { headHTML, actionsBarHTML, panesHTML, lifeStagesHTML, treadHistoryHTML, timelineHTML, rotatePanelHTML } from './tyredetail.view.js';

const HISTORY_PAGE = 50;

/**
 * @param {HTMLElement} viewEl
 * @param {object} ctx
 * @param {object} ctx.tyre        the row object from the register (instant paint)
 * @param {(actionType:string, current:object)=>void} [ctx.onAction] handle a bar
 *        action (edit / fit / move / remove / inspect / repair / retread), given
 *        the freshest record. `rotate` is logged inline here and never reaches it.
 * @returns {() => void} teardown
 */
export function renderTyreDetail(viewEl, ctx = {}) {
  let disposed = false;
  const tyreSeed = ctx.tyre || {};
  const externalTyreId = tyreSeed.externalTyreId;
  let tyre = tyreSeed;
  let actions = [];   // last loaded history; the panes read repair costs from it

  let rotating = false;

  const tpl = document.createElement('template');
  tpl.innerHTML = `
    <div class="tm-detail">
      <div class="tm-detail-head" data-head>${headHTML(tyre)}</div>
      <div class="tm-detail-actions" data-actions>${actionsBarHTML(tyre)}</div>
      ${rotatePanelHTML()}
      <div class="tm-panes" data-panes>${panesHTML(tyre)}</div>
      <div data-life-stages>${treadHistoryHTML(tyre)}${lifeStagesHTML(tyre)}</div>
      <section class="tm-pane tm-pane-full">
        <h3 class="tm-pane-title">Action history</h3>
        <div data-history>${timelineHTML({ loading: !!externalTyreId })}</div>
      </section>
    </div>`;
  const root = tpl.content.firstElementChild;
  const headEl = root.querySelector('[data-head]');
  const panesEl = root.querySelector('[data-panes]');
  const lifeStagesEl = root.querySelector('[data-life-stages]');
  const historyEl = root.querySelector('[data-history]');
  const actionsEl = root.querySelector('[data-actions]');

  const rotatePanel = root.querySelector('[data-rotate-panel]');
  const rotateMsg = root.querySelector('[data-rotate-msg]');
  const rotateDate = root.querySelector('[data-rotate-date]');
  const rotateSaveBtn = root.querySelector('[data-rotate-save]');
  const rotateCancelBtn = root.querySelector('[data-rotate-cancel]');

  // Re-render helpers, shared by the first paint and by the CHANGED subscription
  // at the foot of this function. On refresh the existing timeline stays visible
  // until the new list arrives (no skeleton flash).
  function loadRecord() {
    return fetchTyre(externalTyreId)
      .then((res) => {
        if (disposed) return;
        if (res.success && res.data) {
          tyre = res.data;
          headEl.innerHTML = headHTML(tyre);
          actionsEl.innerHTML = actionsBarHTML(tyre);
          panesEl.innerHTML = panesHTML(tyre, actions);
          lifeStagesEl.innerHTML = treadHistoryHTML(tyre) + lifeStagesHTML(tyre);
        }
      })
      .catch(() => { /* keep the last good paint on failure */ });
  }

  function loadHistory() {
    return fetchTyreActions(externalTyreId, { limit: HISTORY_PAGE, offset: 0 })
      .then((res) => {
        if (disposed) return;
        if (!res.success) {
          historyEl.innerHTML = timelineHTML({ loading: false, error: res.message || 'Could not load action history.' });
          return;
        }
        actions = res.data?.actions || [];
        historyEl.innerHTML = timelineHTML({ loading: false, actions });
        panesEl.innerHTML = panesHTML(tyre, actions);
      })
      .catch(() => {
        if (disposed) return;
        historyEl.innerHTML = timelineHTML({ loading: false, error: 'Could not load action history.' });
      });
  }

  // ---- inline rotation (fitted-only; date is the only input) ----
  function setRotateMsg(text, kind) {
    rotateMsg.textContent = text || '';
    rotateMsg.hidden = !text;
    rotateMsg.className = 'tm-form-msg' + (kind ? ` tm-form-msg-${kind}` : '');
  }

  function setRotateSaving(on) {
    rotating = on;
    rotateSaveBtn.disabled = on;
    rotateCancelBtn.disabled = on;
    rotateSaveBtn.textContent = on ? 'Saving…' : 'Log rotation';
  }

  function openRotatePanel() {
    setRotateSaving(false);          // clean slate on every open
    setRotateMsg('', '');
    const today = todayInputValue();
    rotateDate.value = today;
    rotateDate.max = today;          // a rotation can't be in the future
    rotatePanel.hidden = false;
    rotateDate.focus();
  }

  function closeRotatePanel() {
    rotatePanel.hidden = true;
    setRotateMsg('', '');
  }

  async function saveRotation() {
    if (rotating || !externalTyreId) return;
    const dateVal = rotateDate.value;
    if (!dateVal) { setRotateMsg('Please choose a rotation date.', 'error'); return; }

    setRotateMsg('', '');
    setRotateSaving(true);
    try {
      const res = await createTyreAction(externalTyreId, {
        actionType: apiActionType('rotate'),   // 'rotation'
        action: actionLogMessage('rotate'),    // 'Rotation logged'
        timestamp: isoFromDateInput(dateVal),
        details: {}                            // sensor & positions are server-side
      }, { reason: 'rotate' });
      if (disposed) return;
      if (!res.success) {
        setRotateMsg(res.message || 'Could not log the rotation.', 'error');
        setRotateSaving(false);
        return;
      }
      // createTyreAction has already announced the change; the subscription below
      // repaints the record + timeline. Buttons stay disabled through the brief
      // confirmation until auto-close.
      setRotateMsg('Rotation logged.', 'ok');
      setTimeout(() => { if (!disposed) closeRotatePanel(); }, 900);
    } catch (e) {
      if (!disposed) {
        setRotateMsg('Something went wrong while logging the rotation. Please try again.', 'error');
        setRotateSaving(false);
      }
    }
  }

  // Rotate is handled here (inline); every other action delegates to the caller.
  actionsEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-act]');
    if (!btn) return;
    const act = btn.getAttribute('data-act');
    if (act === 'rotate') { openRotatePanel(); return; }
    if (typeof ctx.onAction === 'function') ctx.onAction(act, tyre);
  });

  rotateSaveBtn.addEventListener('click', saveRotation);
  rotateCancelBtn.addEventListener('click', () => { if (!rotating) closeRotatePanel(); });

  viewEl.replaceChildren(root);

  if (!externalTyreId) {
    historyEl.innerHTML = timelineHTML({ loading: false, actions: [] });
    return () => { disposed = true; };
  }

  loadRecord();
  loadHistory();

  // Subscribe rather than repaint by hand: the inline rotation writes through
  // tyres.api.js, which emits, which lands here. Any CHANGED refreshes, not only
  // ones naming this tyre — freeing a sensor by fitting a different tyre to it
  // changes this record too, and one GET is cheaper than reasoning about which
  // ids a given write implicates.
  const off = onTyreEvent(TYRE_EVENTS.CHANGED, () => {
    loadRecord();
    loadHistory();
  });

  return () => {
    disposed = true;
    off();
  };
}
